import { fetchByIds, fetchOrgMembersWith } from "@/lib/large-org";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { createInvite, revokeInvite } from "@/lib/invites.functions";
import { Copy, Plus, Trash2, MapPin, UserCheck, Clock } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/clients")({
  component: ClientsAdminPage,
  head: () => ({ meta: [{ title: "Client Access — MissionReady" }] }),
});

type Site = { id: string; name: string };
type ClientMember = { user_id: string; email: string | null; sites: Site[] };
type Invite = {
  id: string;
  email: string;
  token: string;
  site_ids: string[] | null;
  accepted_at: string | null;
  expires_at: string | null;
  created_at: string;
};

function ClientsAdminPage() {
  const { currentOrg, currentRole } = useAuth();
  const createInviteFn = useServerFn(createInvite);
  const revokeInviteFn = useServerFn(revokeInvite);
  const [sites, setSites] = useState<Site[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [clients, setClients] = useState<ClientMember[]>([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const isOwner = currentRole === "org_owner";

  const refresh = async () => {
    if (!currentOrg) return;
    const [{ data: siteRows }, { data: inviteRows }, { data: memberRows }] = await Promise.all([
      supabase.from("sites").select("id,name").eq("organization_id", currentOrg.id).order("name"),
      supabase.from("organization_invites")
        .select("id,email,token,site_ids,accepted_at,expires_at,created_at")
        .eq("organization_id", currentOrg.id)
        .eq("role", "client_viewer")
        .order("created_at", { ascending: false }),
      fetchOrgMembersWith(supabase, currentOrg.id, "user_id, profiles(email)", (q) => q.eq("role", "client_viewer")).then((d) => ({ data: d })),
    ]);

    setSites((siteRows ?? []) as Site[]);
    setInvites((inviteRows ?? []) as any);

    const memberIds = (memberRows ?? []).map((m: any) => m.user_id);
    let assignments: any[] = [];
    if (memberIds.length > 0) {
      const data = await fetchByIds<any>("user_site_assignments", "user_id, site_id, sites(id,name), organization_id", memberIds, "user_id");
      assignments = data.filter((a: any) => a.organization_id === currentOrg.id);
    }
    const grouped: ClientMember[] = (memberRows ?? []).map((m: any) => ({
      user_id: m.user_id,
      email: m.profiles?.email ?? null,
      sites: assignments
        .filter((a) => a.user_id === m.user_id && a.sites)
        .map((a) => ({ id: a.sites.id, name: a.sites.name })),
    }));
    setClients(grouped);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentOrg?.id]);

  if (!currentOrg) return <div className="p-8 text-sm text-muted-foreground">Select an organization.</div>;
  if (!isOwner) return <Navigate to="/operations" replace />;

  const toggleSite = (id: string) => {
    setSelectedSites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSites.size === 0) { toast.error("Pick at least one site for this client."); return; }
    setBusy(true);
    try {
      const res: any = await createInviteFn({
        data: {
          orgId: currentOrg.id,
          email: email.trim().toLowerCase(),
          role: "client_viewer",
          siteIds: Array.from(selectedSites),
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
        },
      });
      const link = `${window.location.origin}/accept-invite/${res.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      if (res?.emailSent) toast.success(`Invite emailed to ${email.trim().toLowerCase()} — link also copied.`);
      else toast.warning(`Invite created, but the email couldn't be sent${res?.emailError ? ` (${res.emailError})` : ""}. Link copied — share it directly.`);

      setEmail(""); setSelectedSites(new Set()); setOpen(false);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to send invite");
    } finally {
      setBusy(false);
    }
  };

  const removeAssignment = async (userId: string, siteId: string) => {
    const { error } = await supabase.from("user_site_assignments")
      .delete()
      .eq("organization_id", currentOrg.id)
      .eq("user_id", userId)
      .eq("site_id", siteId);
    if (error) toast.error(error.message); else { toast.success("Site access removed"); refresh(); }
  };

  const addAssignment = async (userId: string, siteId: string) => {
    const { error } = await supabase.from("user_site_assignments")
      .insert({ organization_id: currentOrg.id, user_id: userId, site_id: siteId });
    if (error) toast.error(error.message); else { toast.success("Site access granted"); refresh(); }
  };

  const copyInvite = async (token: string) => {
    await navigator.clipboard.writeText(`${window.location.origin}/accept-invite/${token}`);
    toast.success("Invite link copied");
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Client Access</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Invite clients to the read-only portal and manage which sites they can see.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2 h-4 w-4" />Invite client</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite a client</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@company.com" />
              </div>
              <div>
                <Label>Sites this client can see</Label>
                <div className="mt-2 max-h-56 space-y-2 overflow-y-auto rounded-md border border-border p-3">
                  {sites.length === 0 && <p className="text-xs text-muted-foreground">No sites yet — create one first.</p>}
                  {sites.map((s) => (
                    <label key={s.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox checked={selectedSites.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy || sites.length === 0}>{busy ? "Sending…" : "Send invite"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold text-muted-foreground">Active clients</h2>
        <div className="rounded-xl border border-border bg-card">
          {clients.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">No clients yet.</div>
          ) : clients.map((c) => (
            <div key={c.user_id} className="border-b border-border p-4 last:border-b-0">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-primary" />
                  <span className="font-medium">{c.email ?? c.user_id.slice(0, 8) + "…"}</span>
                </div>
                <Badge variant="outline">{c.sites.length} site{c.sites.length === 1 ? "" : "s"}</Badge>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.sites.map((s) => (
                  <Badge key={s.id} variant="secondary" className="gap-1">
                    {s.name}
                    <button onClick={() => removeAssignment(c.user_id, s.id)} className="ml-1 text-muted-foreground hover:text-destructive" title="Remove access">
                      ×
                    </button>
                  </Badge>
                ))}
                <select
                  className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value=""
                  onChange={(e) => { if (e.target.value) addAssignment(c.user_id, e.target.value); }}
                >
                  <option value="">+ Add site…</option>
                  {sites.filter((s) => !c.sites.find((cs) => cs.id === s.id)).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-muted-foreground">Pending invites</h2>
        <div className="rounded-xl border border-border bg-card">
          {invites.filter((i) => !i.accepted_at).length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">No pending invites.</div>
          ) : invites.filter((i) => !i.accepted_at).map((i) => (
            <div key={i.id} className="flex flex-wrap items-center gap-3 border-b border-border p-3 last:border-b-0">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{i.email}</div>
                <div className="text-xs text-muted-foreground">
                  {(i.site_ids ?? []).length} site{(i.site_ids ?? []).length === 1 ? "" : "s"} pre-assigned
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => copyInvite(i.token)}><Copy className="mr-1 h-3 w-3" />Copy link</Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-destructive"
                onClick={async () => { await revokeInviteFn({ data: { inviteId: i.id } }); refresh(); }}
                title="Revoke"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
